import { Loader2 } from 'lucide-react';
import { useCallback, useId, useTransition } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information.svg';

import { Checkbox } from './checkbox';
import { Label } from './label';
import { messages } from './messages';
import { Tooltip } from './tooltip';
import { Typography } from './typography';

interface AggregateDataCheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  isDisabled?: boolean;
  label: React.ReactNode;
  tooltipContent?: React.ReactNode;
}

export function AggregateDataCheckbox({
  checked,
  onCheckedChange,
  isDisabled = false,
  label,
  tooltipContent,
}: AggregateDataCheckboxProps) {
  const intl = useIntl();
  const inputId = useId();
  const [isPending, startTransition] = useTransition();

  const dispatchChange = useCallback(
    (nextChecked: boolean) => {
      startTransition(() => onCheckedChange(nextChecked));
    },
    [startTransition, onCheckedChange],
  );

  const handleCheckedChange = (value: boolean | 'indeterminate') => {
    if (typeof value !== 'boolean') return;
    dispatchChange(value);
  };

  return (
    <div
      className={`flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <span className="size-5 shrink-0">
        {isPending ? (
          <Loader2 className="text-fx-primary size-5 shrink-0 animate-spin" />
        ) : (
          <Checkbox
            id={inputId}
            checked={checked}
            onCheckedChange={handleCheckedChange}
            disabled={isDisabled}
          />
        )}
      </span>
      <Label
        htmlFor={inputId}
        className={isDisabled ? 'pointer-events-none' : undefined}
      >
        <Typography variant="body1">
          {label ?? (
            <FormattedMessage
              defaultMessage="Include aggregate hires"
              description="Default label: combine hires across linked WX openings in funnel"
            />
          )}
        </Typography>
      </Label>
      {tooltipContent && (
        <Tooltip
          content={
            tooltipContent ??
            intl.formatMessage({
              defaultMessage:
                'For openings that share sourcing efforts, show the aggregate number of hires and budget spend across all openings in the hiring workflow',
              description:
                'Default info-tooltip when no custom tooltip: explains WX-linked rollup',
            })
          }
        >
          <img src={InfoIcon} alt={intl.formatMessage(messages.information)} />
        </Tooltip>
      )}
    </div>
  );
}
